'use es6';

import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { sanitize } from 'sanitize-text/sanitizers/Sanitize';
import { config } from 'sanitize-text/sanitizers/SanitizeConfiguration';
import ConfigurationPanelHeader from 'FormsUI/components/editor/form/configuration/ConfigurationPanelHeader';
import PaymentConfigurationPanelHeader from 'FormsUI/components/editor/form/configuration/headers/Payment';
import { getSupportedFormFieldTypeOrText } from 'FormsUI/utils/FormFieldsUtils';
import { getDependentFieldForId } from 'FormsUI/selectors/FormSmartOptions';
import { getObjectById } from 'FormsUI/selectors/customObjects';
import { canBeProfilled, canAddDependent } from 'FormsUI/selectors/Field';
import { FieldTypes } from 'ui-forms-components/constants/FieldTypeMapping';
import { Link } from 'foundations-components/transitional/components/Link';
import { getFormField } from 'FormsUI/selectors/FormDefinition';
import { getAbmPropertyRoute } from 'FormsUI/lib/RouteGenerators';
import { getObjectTypeIdValue, getObjectTypeLabel } from 'FormsCommonComponents/form-definition/utils/FormFieldUtils';
import FormFieldConfigurationTabs from 'FormsUI/components/editor/form/configuration/FormFieldConfigurationTabs';
import { ConfigPanelWrapper } from 'FormsUI/components/editor/form/configuration/ConfigPanelWrapper';
import { FormFieldConfigHeader } from 'FormsUI/components/editor/form/configuration/FormFieldConfigHeader';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const langKey = suffix => `editor.form.config.${suffix}`;
export const PostAbmSidePanelHeaderLabel = ({
  objectTypeLabel = ''
}) => /*#__PURE__*/_jsx(FormattedReactMessage, {
  message: langKey('newHeader.sub.anyPropertyTitle'),
  options: {
    objectName: objectTypeLabel
  }
});
export const PostAbmSidePanelHeaderLink = ({
  objectTypeLabel = ''
}) => /*#__PURE__*/_jsx(FormattedReactMessage, {
  message: langKey('panelHeader.editAnyProperty'),
  options: {
    objectName: objectTypeLabel.toLocaleLowerCase()
  }
});
class FormFieldConfigurationPanel extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      selected: 'basic'
    };
    this.handleSelectedChange = this.handleSelectedChange.bind(this);
    this.maybeRenderNewBackLink = this.maybeRenderNewBackLink.bind(this);
    this.renderHeaderUILink = this.renderHeaderUILink.bind(this);
    this.renderHeader = this.renderHeader.bind(this);
  }
  handleSelectedChange(tabId) {
    this.setState({
      selected: tabId
    });
  }
  langKey(suffix) {
    return `editor.form.config.${suffix}`;
  }
  maybeRenderNewBackLink(name, objectTypeId, fieldType) {
    const {
      hideConfigurationPanel
    } = this.props;
    switch (fieldType) {
      case FieldTypes.PAYMENT_RADIO:
        return /*#__PURE__*/_jsx(PaymentConfigurationPanelHeader, {
          handleDone: hideConfigurationPanel
        });
      default:
        return /*#__PURE__*/_jsx(ConfigurationPanelHeader, {
          handleDone: hideConfigurationPanel,
          customLink: this.renderHeaderUILink(name, objectTypeId)
        });
    }
  }
  renderHeaderUILink(name, objectTypeId) {
    const {
      objectType: {
        objectTypeLabel = ''
      } = {}
    } = this.props;
    const label = getObjectTypeLabel(objectTypeLabel, objectTypeId);
    const linkHref = getAbmPropertyRoute(name, objectTypeId);
    return /*#__PURE__*/_jsx(Text, {
      textTransform: "capitalize",
      children: /*#__PURE__*/_jsx(Link, {
        use: "blue",
        href: linkHref,
        external: true,
        "data-test-id": "property-edit-sidebar-header-link",
        children: /*#__PURE__*/_jsx(PostAbmSidePanelHeaderLink, {
          objectTypeLabel: label
        })
      })
    });
  }
  renderHeader(name, label, objectTypeId, fieldType, renderObjectType = true) {
    const {
      objectType: {
        objectTypeLabel = ''
      } = {}
    } = this.props;
    const objectTypeLabelHeader = getObjectTypeLabel(objectTypeLabel, objectTypeId);
    return /*#__PURE__*/_jsxs(View, {
      paddingBlockStart: "1200",
      children: [/*#__PURE__*/_jsx(FormFieldConfigHeader, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: langKey('newHeader.title'),
          options: {
            propertyText: sanitize(label || name, config.TEXTONLY)
          }
        })
      }), /*#__PURE__*/_jsxs(View, {
        padding: "600",
        children: [renderObjectType && /*#__PURE__*/_jsx(FormControl, {
          "data-test-id": "property-edit-sidebar-header-label",
          label: /*#__PURE__*/_jsx(PostAbmSidePanelHeaderLabel, {
            objectTypeLabel: objectTypeLabelHeader
          }),
          children: /*#__PURE__*/_jsx("span", {
            children: name
          })
        }), /*#__PURE__*/_jsx(FormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: langKey('newHeader.sub.fieldTitle')
          }),
          children: /*#__PURE__*/_jsx("span", {
            children: fieldType && /*#__PURE__*/_jsx(FormattedMessage, {
              message: `editor.form.properties.createNewFieldSection.fieldTypes.${fieldType}`
            })
          })
        })]
      })]
    });
  }
  render() {
    const {
      formField,
      dependentField,
      shouldShowLogicConfig,
      shouldLockLogicConfig,
      renderObjectType
    } = this.props;
    const {
      selected
    } = this.state;
    const field = formField || dependentField;
    if (isEmpty(field)) {
      return null;
    }
    const fieldType = getSupportedFormFieldTypeOrText(field);
    const objectTypeId = getObjectTypeIdValue(field.objectTypeId, field.propertyObjectType);
    return /*#__PURE__*/_jsxs(ConfigPanelWrapper, {
      "data-commenting-scroll-container": true,
      children: [this.maybeRenderNewBackLink(field.name, objectTypeId, fieldType), this.renderHeader(field.name, field.label, objectTypeId, fieldType, renderObjectType), /*#__PURE__*/_jsx(View, {
        padding: "400",
        children: /*#__PURE__*/_jsx(FormFieldConfigurationTabs, {
          field: field,
          shouldLockLogicConfig: shouldLockLogicConfig,
          shouldShowLogicConfig: shouldShowLogicConfig,
          selected: selected,
          onSelectedChange: this.handleSelectedChange
        })
      })]
    }, `properties-panel-${field.name}`);
  }
}
FormFieldConfigurationPanel.propTypes = {
  formField: PropTypes.object,
  formFieldId: PropTypes.string,
  dependentField: PropTypes.object,
  hideConfigurationPanel: PropTypes.func.isRequired,
  shouldShowLogicConfig: PropTypes.bool.isRequired,
  shouldLockLogicConfig: PropTypes.bool,
  renderObjectType: PropTypes.bool,
  objectType: PropTypes.object
};
const mapStateToProps = (state, props) => {
  const formField = getFormField(state, props);
  const dependentField = getDependentFieldForId(state, props);
  return Object.assign({
    formField,
    dependentField,
    shouldShowLogicConfig: canBeProfilled(state) || canAddDependent(state)
  }, !!(formField || dependentField) && {
    objectType: getObjectById(state, (formField || dependentField).objectTypeId)
  });
};
export default connect(mapStateToProps)(FormFieldConfigurationPanel);